import { useCallback, useEffect, useState } from "react";
import { Link } from "wouter";
import Sidebar from "@/components/Sidebar";
import { apiJson } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";

type RevRow = {
  id: string;
  document_key: string;
  revision_no: number;
  created_by: number | null;
  created_at: string;
};

const DOCS = [
  { value: "projects", label: "Проекты" },
  { value: "gantt", label: "Гант программы" },
  { value: "review_snapshots", label: "Снимки отчётности" },
];

export default function HistoryPage() {
  const { canEdit } = useAuth();
  const [documentKey, setDocumentKey] = useState("projects");
  const [revisions, setRevisions] = useState<RevRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    const data = await apiJson<{ revisions: RevRow[] }>(
      `/api/revisions?documentKey=${encodeURIComponent(documentKey)}&limit=50`,
    );
    setRevisions(data.revisions);
  }, [documentKey]);

  useEffect(() => {
    if (!canEdit) return;
    load().catch((e) => setError(String(e)));
  }, [load, canEdit]);

  async function rollback(id: string) {
    if (!confirm("Восстановить состояние из этой ревизии? Текущее будет сохранено как новая ревизия.")) return;
    setError(null);
    setMsg(null);
    try {
      await apiJson("/api/revisions/rollback", {
        method: "POST",
        body: JSON.stringify({ revisionId: id }),
      });
      setMsg("Откат выполнен. Обновите страницу дашборда.");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ошибка");
    }
  }

  if (!canEdit) {
    return (
      <div className="min-h-screen flex" style={{ background: "var(--background)" }}>
        <Sidebar />
        <main className="flex-1 p-8 transition-[margin-left] duration-200 ease-out [margin-left:var(--app-sidebar-width)]">
          <p>История и откат доступны только редакторам и администраторам.</p>
          <Link href="/">На главную</Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ background: "var(--background)" }}>
      <Sidebar />
      <main className="max-w-4xl flex-1 p-8 transition-[margin-left] duration-200 ease-out [margin-left:var(--app-sidebar-width)]">
        <div className="mb-6 flex items-center justify-between gap-4">
          <h1 className="text-2xl font-bold">История изменений и откат</h1>
          <Button variant="outline" asChild>
            <Link href="/">На главную</Link>
          </Button>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          Перед каждым сохранением документа предыдущая версия попадает в ревизии. Откат восстанавливает выбранный
          снимок (доступно admin и editor).
        </p>

        {error ? <p className="text-red-600 text-sm mb-4">{error}</p> : null}
        {msg ? <p className="text-green-700 text-sm mb-4">{msg}</p> : null}

        <div className="flex items-center gap-4 mb-6">
          <div className="space-y-2">
            <Label>Документ</Label>
            <Select value={documentKey} onValueChange={setDocumentKey}>
              <SelectTrigger className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOCS.map((d) => (
                  <SelectItem key={d.value} value={d.value}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>№</TableHead>
              <TableHead>Создано</TableHead>
              <TableHead>Кем (user id)</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {revisions.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{r.revision_no}</TableCell>
                <TableCell className="text-xs whitespace-nowrap">{r.created_at}</TableCell>
                <TableCell>{r.created_by ?? "—"}</TableCell>
                <TableCell className="text-right">
                  <Button type="button" size="sm" variant="secondary" onClick={() => rollback(r.id)}>
                    Откатить сюда
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </main>
    </div>
  );
}
